// web-app/src/utils/cornerJoinMigration.js
//
// Read-time migration (layout-relationships §5.2, #1494): a room whose
// generator-derived runs were persisted PER WALL — one `autorun_<wallId>`
// each — gets them joined at the corner the layout actually put a corner
// cabinet in, the same rule `cabinetLayoutToRuns` applies to a fresh apply.
// The absorbed run's objects are re-tagged to the survivor and the corner
// cabinet becomes the run's. Pure; identity when nothing joins. Written back
// by whichever room-level rewrite runs next (today: the wall-move regen).

import { joinRunsAtCorners, storedRunFromRun } from '@shared-lib/cabinet-runs';
import { healRunBayClaims } from '@shared-lib/cabinet-runs/bays';
import { buildCabinetWallResolver } from '@shared-lib/kitchen-assembly/cabinetLayout/resolveCabinetWall';

import { cornerUnitWallIds } from '@shared-lib/cabinet-runs/cornerUnit';
import {
  withCornerTreatments,
  applianceSeparatesLegs,
} from '@shared-lib/cabinet-runs/cornerTreatment';

/**
 * @param {Object} room
 * @returns {{ room: Object, joined: string[][] }} `joined` lists the run-id
 *   pairs that became one.
 */
// The corner pieces a joined run owns: the L / single-sided unit, and the
// oblique corner's kite with the slab over it (`toRuns.OWNABLE_BAY_TYPES`).
const CORNER_PIECE_TYPES = new Set([
  'corner-cabinet',
  'corner-fill-cabinet',
  'corner-fill-countertop',
]);

export function joinPersistedAutoRuns(roomInput) {
  // Heal double-claimed bays FIRST (a phantom wall-run bay naming an island
  // appliance - the freestanding claim wins), so the healed runs persist on
  // the next room write whatever else this pass decides.
  const healedRuns = healRunBayClaims(roomInput?.cabinetRuns);
  const room =
    healedRuns === (roomInput?.cabinetRuns || [])
      ? roomInput
      : { ...roomInput, cabinetRuns: healedRuns };
  const stored = room?.cabinetRuns || [];
  // Real walls and peninsula arms — the same set `cabinetLayoutToRuns` joins
  // on a fresh apply. Islands stay on their own builder.
  const JOINABLE_KINDS = new Set(['real', 'peninsula']);
  // A wall the layout put no run on (`carriesRun: false` — a detached
  // fridge's parking wall) never joins: its run is the appliance alone.
  const runlessWallIds = new Set(
    (room?.cabinetLayout?.cabinetWalls || [])
      .filter((cw) => cw?.carriesRun === false)
      .map((cw) => cw.id)
  );
  const candidates = stored.filter(
    (r) =>
      r?.source === 'auto' &&
      (r.segments || []).length === 1 &&
      JOINABLE_KINDS.has(r.segments[0].kind || 'real') &&
      !r.segments[0].freestanding &&
      !runlessWallIds.has(r.segments[0].wallId)
  );
  if (candidates.length < 2) return { room, joined: [] };

  const resolver = buildCabinetWallResolver(room, room?.cabinetLayout);
  const resolved = candidates.map((r) => ({
    ...r,
    segments: r.segments.map((s) => ({
      ...s,
      wall: resolver.resolve({ id: s.wallId, kind: s.kind || 'real' }),
    })),
  }));
  // An appliance standing at the vertex ends the run — the same rule
  // `cabinetLayoutToRuns` applies on a fresh apply.
  const { runs, joins } = joinRunsAtCorners(resolved, {
    refuse: (segA, segB) => applianceSeparatesLegs(segA, segB, room.objects),
  });
  if (!joins.length) return { room, joined: [] };

  // A counter that turns is one run whatever stands in the box: every
  // interior vertex records the treatment the layout gave it (an L, a blind
  // unit, or an empty box) so the re-solve reproduces it — the same rule
  // `cabinetLayoutToRuns` applies on a fresh apply.
  const accepted = runs
    .filter((r) => r.joinedRunIds)
    .map((r) =>
      withCornerTreatments(r, room.objects, room?.cabinetLayout?.corners)
    );
  if (!accepted.length) return { room, joined: [] };

  const absorbedTo = new Map(); // absorbed run id → survivor id
  const replaced = new Set();
  for (const r of accepted) {
    for (const id of r.joinedRunIds) {
      replaced.add(id);
      if (id !== r.id) absorbedTo.set(id, r.id);
    }
  }
  const merged = accepted.map((r) => {
    const { joinedRunIds, ...rest } = r;
    // A run that turns from a wall onto a peninsula keeps its uppers on the
    // wall; `fillRun` hangs none over the arm by the segment's `isVirtual`.
    // The lone peninsula run's `uppers:false` would switch the wall's off.
    const kinds = new Set(rest.segments.map((s) => s.kind || 'real'));
    const { uppers, ...sansUppers } = rest;
    return storedRunFromRun({
      ...(kinds.size === 1 ? rest : sansUppers),
      segments: rest.segments.map(({ wall, ...s }) => s),
    });
  });
  const cabinetRuns = [
    ...stored.filter((r) => !replaced.has(r?.id)),
    ...merged,
  ];
  const survivorWalls = new Map(
    merged.map((r) => [r.id, new Set(r.segments.map((s) => s.wallId))])
  );
  const objects = (room.objects || []).map((o) => {
    if (o?.runId && absorbedTo.has(o.runId)) {
      return { ...o, runId: absorbedTo.get(o.runId) };
    }
    if (
      CORNER_PIECE_TYPES.has(o?.type) &&
      !o.runId &&
      o.dimensions?.isUpper !== true
    ) {
      const legs = cornerUnitWallIds(o);
      for (const [id, wallsOf] of survivorWalls) {
        if (legs.length >= 2 && legs.every((w) => wallsOf.has(w))) {
          return { ...o, runId: id };
        }
      }
    }
    return o;
  });
  return {
    room: { ...room, cabinetRuns, objects },
    joined: accepted.map((r) => r.joinedRunIds),
  };
}
